import angular from "angular";
import { SxyTableController, SxyTableOptions } from "../sxy-table";
import { ColumnDefinition } from "tabulator-tables";
import { Arrays, CellRenderingLogic, EnumerationsLogic, AccountInIndex, OpenAPIV3, Translations, JsonSchemaUtils } from "@salaxy/core";
import { JsonSchemaService } from "../../services";

/**
 * Used for running batch processes to the server.
 * At the time of writing, the controller isonly used in Modal dialogs, but may�be used for something else later.
 */
export class BatchProcessController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$uibModalInstance", "data", "$sce", "$timeout", "JsonSchemaService"];

  /** Counter and status for the processing of the items */
  public processCounter: {
    /** If true, the processing is in progress (started and not yet done). */
    isProcessing: boolean,
    /** If true, the processing is done. Note that this may also mean that the processing has been canceled by the user. */
    isDone: boolean,
    /** Total count of items that should be processed. */
    totalCount: number,
    /** Count of items that have been processed. Note that also errors are counted as processed. */
    processedCount: number,
    /** Errors that occured. */
    errorsCount: number,
    /** If true, the process has been canceled by the user. If also isDone is true, the canceling has completed. */
    canceling: boolean,
  };

  /** Options for the modal dialog */
  public dialogHeader: string;

  /** Columns that are shown in the grid. Leave empty use default columns */
  public columns: string | (string | ColumnDefinition)[];

  /** List of items that should be processed. */
  public list: any[];

  /** Usecase specific data for the dialog. */
  public data: any;

  /**
   * Method that is called by the process.
   * ItemOrArray is the item/array that is processed, Data is the controller data that may be edited in the form.
   * The promise may return a message that is shown after text "OK".
   * Promise rejection is cached.
   */
  public method: (itemOrArray: any | any[], data: any) => Promise<({message: string, result: any[]}[])>;

  /**
   * Optional chunk size for the batch process. This is used if the method supports arrays.
   */
  public chunkSize: number;

  /** Start process right after opening the invoice. */
  public autoStart: boolean;

  /** Close dialog after process. */
  public autoClose: boolean;

  /** Options that are bound to the grid.  */
  public tableOptions: SxyTableOptions;

  /** Inner template that is included for usecase specific form and other UI. */
  public batchTemplateUrl: string;

  /** Binding for start button, which launches the processing. */
  public isProcessStarted = false;

  /** The table controller after it has been initialized. */
  public $table: SxyTableController;

  /** References a schema document directly: the schema document is not fetched from the UI */
  public openApiDoc: any;

  /**
   * URL for the Open API document that contains the definition for the type.
   * The url can be an absolute url, relative url or an url with salaxy-secure/ -prefix.
   * The salaxy-secure/ -prefix will be replaced in runtime with the salaxy api server url of the curren environment.
   */
  public openApi = "salaxy-secure/schemas/v02.json";

  /** The schema model */
  public schemaModel: OpenAPIV3.SchemaObject;

  /** If true, the schema loading is ready. */
  public isReady: boolean;

  /**
   * Constructor for dependency instance.
   * @param $uibModalInstance Modal instance supports functions such as close(result), dismiss(reason) and promises result, opened, closed and rendered.
   * @param data Rest of the data injected from the caller.
   */
  constructor(protected $uibModalInstance: any, data: any, $sce: angular.ISCEService, private $timeout: angular.ITimeoutService, private jsonSchemaService: JsonSchemaService) {
    this.data = data;
    this.dialogHeader = this.data.dialogHeader || Translations.get("SALAXY.NG1.BatchProcessComponent.title");
    this.list = this.data.dialogList || [];
    this.columns = this.getColumns(this.data.gridColumns);
    this.method = this.data.batchMethod;
    this.chunkSize = this.data.chunkSize ?? 0;
    this.autoStart = this.data.autoStart ?? false;
    this.autoClose = this.data.autoClose ?? false;
    this.tableOptions = this.getGridOptions();
    this.batchTemplateUrl = $sce.trustAsResourceUrl(data.batchTemplateUrl);
  }

  /** Initialization */
  public $onInit = () => {
    this.resetProcessed();
    if (this.autoStart === true) {
      this.startProcess();
    }
    if (this.data.listItemType) {
      if (this.openApiDoc) {
        this.jsonSchemaService.addSchemaDocument(this.openApi, this.openApiDoc);
        this.schemaModel = this.jsonSchemaService.findSchema(this.openApi, this.data.listItemType);
        this.isReady = true;
      } else {
        this.jsonSchemaService.assureSchemaDocument(this.openApi).then((openApiDoc) => {
          this.openApiDoc = openApiDoc;
          this.schemaModel = this.jsonSchemaService.findSchema(this.openApi, this.data.listItemType);
          this.isReady = true;
        });
      }
    } else {
      this.isReady = true;
    }
  };

  /** Starts the processing */
  public startProcess() {
    // Prevent double clicks
    if (this.isProcessStarted) {
      return;
    }
    this.isProcessStarted = true;
    this.resetProcessed();
    this.processNextItems();
  }

  /** Processes next items in the queue */
  public processNextItems() {
    this.$timeout(() => {
      this.processNextItemsInternal();
      // TODO: Consider adding this method to the SxyTableController in addition of refresh(). which redraws the table.
      try {
        this.$table.tabulator.replaceData(this.list);
      } catch (e) {
        // Ignore, the table is not initialized yet.
      }
    });
  }

  private processNextItemsInternal() {
    const counter = this.processCounter;
    counter.isProcessing = true;
    if (counter.canceling) {
      counter.isProcessing = false;
      counter.isDone = true;
      return;
    }
    const remainingItems = this.list.filter((x) => !x.processed);
    if (remainingItems.length === 0) {
      counter.isProcessing = false;
      counter.isDone = true;
      this.data.done = true;
      if (this.autoClose) {
        this.close();
      }
    } else {
      const itemsToProcess = remainingItems.slice(0, this.chunkSize > 0 ? this.chunkSize : 1);
      for (const itemToProcess of itemsToProcess) {
        itemToProcess.processed = Translations.get("SALAXY.NG1.BatchProcessComponent.processing");
      }
      this.method(this.chunkSize > 0 ? itemsToProcess : itemsToProcess[0], this.data)
        .then((results) => {
          let i = 0;
          for (const itemToProcess of itemsToProcess) {
            itemToProcess.processed = "OK " + (results[i].message);
            itemToProcess.processedResult = { message: results[i].message, result: results[i].result };
            i++;
          }
          counter.processedCount += itemsToProcess.length;
          this.processNextItems();
        })
        .catch((reason) => {
          let label = "Server error";
          let msg = "Unknown error";
          if (reason) {
            msg = reason.statusText || msg;
            if (reason.data) {
              label = reason.data.error || label;
              msg = reason.data.messageHtml || msg;
            }
          }
          for (const itemToProcess of itemsToProcess) {
            itemToProcess.processed = "ERR " + label + ". " + msg;
            itemToProcess.processedResult = null;
          }
          console.error(label, reason);
          counter.processedCount += itemsToProcess.length;
          counter.errorsCount += itemsToProcess.length;
          this.processNextItems();
        });
    }
  }

  /** Closes the dialog. */
  public close() {
    this.resetProcessed();
    this.$uibModalInstance.close();
  }

  /** Resets the processed status column(s) */
  public resetProcessed() {
    for (const item of this.list) {
      if (item.processed) {
        item.processed = null;
      }
    }
    this.processCounter = {
      isProcessing: false,
      isDone: false,
      totalCount: this.list.length,
      processedCount: 0,
      errorsCount: 0,
      canceling: false,
    };
  }

  /** Cancels the process: Waits for the next response, but does not start a new one. */
  public cancelProcess() {
    this.processCounter.canceling = true;
  }

  private getColumns(columns: string | (string | ColumnDefinition)[]): string | (string | ColumnDefinition)[] {
    const defaultCols: ColumnDefinition[] = [
      ...this.getAvatarCols("owner", Translations.get("SALAXY.NG1.BatchProcessComponent.employer"), { width: 300 }),
      ...this.getAvatarCols("other", Translations.get("SALAXY.NG1.BatchProcessComponent.worker"), { width: 400 }),
      {
        field: "status",
        title: Translations.get("SALAXY.NG1.BatchProcessComponent.status"),
        formatter: (cell: any) => {
          const status = cell.getData().status;
          const statusEnum = JsonSchemaUtils.getProperty(this.schemaModel, "status")?.schema?.format;
          if (!status) {
            return `<div>-</div>`;
          }
          if (!statusEnum) {
            return `<div>${status}</div>`;
          }
          return Translations.get(`SALAXY.ENUM.${statusEnum}.${status}.label`);
        }
      },
    ];
    if (typeof columns === "string") {
      columns = Arrays.assureArray(columns);
    }
    if (!columns?.length) {
      columns = defaultCols;
    }
    if (!columns.find((x) => x == "processed" || (x as ColumnDefinition).field === "processed")) {
      columns.splice(0, 0,{
        width: 400,
        field: "processed",
        title: Translations.get("SALAXY.NG1.BatchProcessComponent.processed"),
        // TODO: cellTemplate: "salaxy-aspa-components/grid-col/processed.html",
        // eslint-disable-next-line
        // cellTooltip: (row, col) => row.entity.processed,
      });
    }
    return columns;
  }

  /**
   * Gets a column definition for avatar column: Renders an avatar with sortable name and second info line.
   * @param party The party to render: either "owner" (ownerInfo) or "other" (otherPartyInfo).
   * @param title The title of the column.
   * @param props Other properties that should be set on the column.
   */
  public getAvatarCols(party: "owner" | "other", title: string, props?: any): any[] {
    const infoProp = party === "other" ? "otherPartyInfo" : "ownerInfo";
    const result: any[] = [
      {
        field: `${infoProp}.avatar.sortableName`, title,
        headerFilter: true,
        formatter: (cell: any) => {
          const info: AccountInIndex = cell.getData()[infoProp];
          return `
              <div class="tabulator-avatar">${CellRenderingLogic.getAvatar(info?.avatar)}${info?.avatar?.sortableName || "-"}</div>
          `;
        },
        ...props,
      },
    ];
    return result;
  }

  /**
   * Gets a column definition for enumerations column
   * @param field The field name / path in the data model.
   * @param title The title of the column.
   * @param enumType The type name of the enumeration.
   * @param props Other properties that should be set on the column.
   */
  public getEnumColumn(field: string, title: string, enumType: string, props?: any): any {
    return {
      field, title,
      formatter: (cell: any) => EnumerationsLogic.getEnumLabel(enumType, cell.getValue()),
      headerFilter: "list",
      headerFilterFunc: "=",
      headerFilterParams: {
        values: EnumerationsLogic.getEnumMetadata(enumType)?.values.map(v => ({ label: v.label, value: v.name })),
      },
      ...props,
    };
  }

  /** Gets the options for the UI grid */
  private getGridOptions(): SxyTableOptions {
    const options: SxyTableOptions = {
      height: "50vh",
      layout: "fitColumns"
    };
    return options;
  }
}
